import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../api/client";
import { products as fallbackProducts } from "../data/product";

const ProductsContext = createContext(null);

// The backend stores brand/category/type/tag as arrays (a product can
// belong to more than one), but the storefront's filter UI was built
// around single flat strings. Rather than rewire every component, this
// takes the first value of each array — every real product only has one
// value in each of these fields anyway, so nothing is lost today, and it
// keeps this the one place that would need to change if that ever isn't
// true.
function normalizeProduct(apiProduct) {
  const images = apiProduct.imageUrl?.length ? apiProduct.imageUrl : [];
  return {
    id: apiProduct._id,
    tag: apiProduct.tag?.[0] || "",
    brand: apiProduct.brand?.[0] || "",
    category: apiProduct.category?.[0] || "",
    type: apiProduct.type?.[0] || "",
    image: images[0] || null,
    images,
    videos: apiProduct.videoUrl || [],
    title: apiProduct.title,
    sku: apiProduct.sku || "",
    price: Number(apiProduct.price) || 0,
    shippingCost: Number(apiProduct.shippingCost) || 0,
    desc: apiProduct.desc,
    moq: `${apiProduct.moq} pcs`,
    moqUnits: Number(apiProduct.moq) || 0,
    lead: apiProduct.lead,
    barcode: apiProduct.barcode || null,
    qrCode: apiProduct.qrCode || null,
    discountPercent: Number(apiProduct.discountPercent || 0),
    discountLabel: apiProduct.discountLabel || "",
    // Not yet stored by the backend — DetailedProducts.jsx falls back to
    // sensible generic defaults when these are absent, so passing them
    // through here now costs nothing and means zero extra wiring the day
    // the CMS grows support for editing them.
    notes: apiProduct.notes,
    packSizes: apiProduct.packSizes,
    volumeTiers: apiProduct.volumeTiers,
    tabs: apiProduct.tabs,
  };
}

export function ProductsProvider({ children }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [usingFallback, setUsingFallback] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await api.listAllProducts();
      setProducts((result.products || []).map(normalizeProduct));
      setUsingFallback(false);
    } catch (err) {
      // The live catalog is unreachable (backend not running, wrong API
      // URL, etc). Rather than show a broken storefront, fall back to the
      // bundled demo data so the site is still browsable — but say so
      // loudly in the console for whoever's debugging deployment.
      console.warn(
        `[ProductsContext] Could not load the live catalog (${err.message}). Falling back to bundled demo data.`
      );
      setProducts(fallbackProducts);
      setUsingFallback(true);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <ProductsContext.Provider value={{ products, loading, error, usingFallback, refetch: load }}>
      {children}
    </ProductsContext.Provider>
  );
}

export function useProducts() {
  const ctx = useContext(ProductsContext);
  if (!ctx) throw new Error("useProducts must be used within ProductsProvider");
  return ctx;
}
